import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Scale, ArrowRight, Mail, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

// Permanent public navigation (single source of truth)
export const PUBLIC_NAV = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Products', path: '/products' },
  { label: 'Help', path: '/help' },
  { label: 'Contact', path: '/contact' },
];

export const FOOTER_NAV = {
  'VSKR Legal': [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Products', path: '/products' },
    { label: 'LIOS', path: '/products/lios' },
  ],
  'Help & Legal': [
    { label: 'Help & Support', path: '/help' },
    { label: 'Contact', path: '/contact' },
    { label: 'Privacy', path: '/privacy' },
    { label: 'Terms', path: '/terms' },
    { label: 'Account & Data Deletion', path: '/account-data-deletion' },
  ],
};

// Owner-configurable public contact (confirm before production)
export const PUBLIC_CONTACT_EMAIL = 'support@vskrlegal.com';

const linkBase = 'text-sm font-medium transition-colors';

const PublicLayout = ({ children, title, description }) => {
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (title) document.title = `${title} | VSKR Legal`;
    if (description) {
      let tag = document.querySelector('meta[name="description"]');
      if (tag) tag.setAttribute('content', description);
    }
  }, [title, description]);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  const isActive = (path) => (path === '/' ? location.pathname === '/' : location.pathname.startsWith(path));

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: isDark ? '#0F172A' : '#FAFBFC', color: isDark ? '#E2E8F0' : '#0F172A' }}
      data-testid="public-layout"
    >
      {/* Ambient background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-[600px] h-[600px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(45,140,255,0.35) 0%, transparent 70%)', top: '-220px', right: '-160px' }} />
        <div className="absolute w-[480px] h-[480px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)', bottom: '-160px', left: '10%' }} />
      </div>

      {/* Header */}
      <header
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{
          background: isDark ? 'rgba(15,23,42,0.72)' : 'rgba(255,255,255,0.72)',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        }}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-logo">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white"
              style={{ background: 'linear-gradient(135deg,#1E3A5F,#2D8CFF)' }}>
              <Scale className="w-5 h-5" />
            </span>
            <span className="text-lg font-bold tracking-tight">VSKR <span className="text-[#D4AF37]">Legal</span></span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {PUBLIC_NAV.map((n) => (
              <Link key={n.path} to={n.path}
                className={`${linkBase} ${isActive(n.path) ? 'text-[#2D8CFF]' : 'hover:text-[#2D8CFF] text-inherit opacity-80 hover:opacity-100'}`}
                data-testid={`public-nav-${n.label.toLowerCase()}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button onClick={toggleTheme} aria-label="Toggle theme" className="p-2 rounded-lg hover:bg-gray-500/10 transition-colors" data-testid="public-theme-toggle">
              {isDark ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4 text-blue-500" />}
            </button>
            <button onClick={() => navigate('/login')}
              className="px-5 py-2 rounded-full text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
              style={{ background: 'linear-gradient(135deg,#1E3A5F,#2D8CFF)' }} data-testid="public-signin-btn">
              Sign In
            </button>
          </div>

          <button className="md:hidden p-2 rounded-lg hover:bg-gray-500/10" onClick={() => setMenuOpen(v => !v)} aria-label="Menu" data-testid="public-menu-toggle">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden border-t" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }} data-testid="public-mobile-menu">
              <nav className="px-5 py-3 flex flex-col gap-1">
                {PUBLIC_NAV.map((n) => (
                  <Link key={n.path} to={n.path} className={`py-2.5 ${linkBase} ${isActive(n.path) ? 'text-[#2D8CFF]' : 'opacity-80'}`}>{n.label}</Link>
                ))}
                <button onClick={() => navigate('/login')} className="mt-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white text-center"
                  style={{ background: 'linear-gradient(135deg,#1E3A5F,#2D8CFF)' }}>Sign In</button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Content */}
      <main className="relative z-10 flex-1">{children}</main>

      {/* Footer */}
      <footer className="relative z-10 border-t mt-16" style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2 max-w-sm">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg,#1E3A5F,#2D8CFF)' }}>
                <Scale className="w-4 h-4" />
              </span>
              <span className="font-bold">VSKR <span className="text-[#D4AF37]">Legal</span></span>
            </div>
            <p className="text-sm opacity-70 leading-relaxed">
              A legal-technology ecosystem building secure, practical and professional tools for legal
              professionals. VSKR Legal brings independent products together under one trusted, unified experience.
            </p>
            <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`} className="inline-flex items-center gap-2 mt-4 text-sm text-[#2D8CFF] hover:underline">
              <Mail className="w-4 h-4" /> {PUBLIC_CONTACT_EMAIL}
            </a>
          </div>
          {Object.entries(FOOTER_NAV).map(([group, items]) => (
            <div key={group}>
              <h4 className="text-xs font-semibold uppercase tracking-wider opacity-60 mb-3">{group}</h4>
              <ul className="space-y-2">
                {items.map((it) => (
                  <li key={it.path}><Link to={it.path} className="text-sm opacity-75 hover:opacity-100 hover:text-[#2D8CFF] transition-colors">{it.label}</Link></li>
                ))}
                {group === 'Help & Legal' && (
                  <li><Link to="/login" className="text-sm opacity-75 hover:opacity-100 hover:text-[#2D8CFF] transition-colors">Login</Link></li>
                )}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t py-5 text-center text-xs opacity-60" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
          © {new Date().getFullYear()} VSKR Legal. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

// Shared premium section helpers
export const PublicHero = ({ eyebrow, title, subtitle, children }) => (
  <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10 lg:pt-24 lg:pb-14">
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
      {eyebrow && <span className="inline-block mb-4 px-3 py-1 rounded-full text-xs font-semibold bg-[#2D8CFF]/10 text-[#2D8CFF]">{eyebrow}</span>}
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">{title}</h1>
      {subtitle && <p className="mt-5 text-base md:text-lg opacity-75 leading-relaxed">{subtitle}</p>}
      {children && <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
    </motion.div>
  </section>
);

export const PublicButton = ({ to, href, children, variant = 'primary', testid }) => {
  const navigate = useNavigate();
  const cls = variant === 'primary'
    ? 'text-white'
    : 'border border-current opacity-80 hover:opacity-100';
  const style = variant === 'primary' ? { background: 'linear-gradient(135deg,#1E3A5F,#2D8CFF)' } : {};
  const onClick = () => { if (to) navigate(to); else if (href) window.location.href = href; };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-transform hover:scale-[1.03] ${cls}`} style={style}>
      {children} <ArrowRight className="w-4 h-4" />
    </button>
  );
};

export const LegalShell = ({ title, updated, children }) => (
  <div className="max-w-3xl mx-auto px-5 lg:px-8 py-14">
    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
    <p className="mt-2 text-sm opacity-60">Last updated: {updated}</p>
    <div className="mt-8 space-y-7 text-sm leading-relaxed opacity-90 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:opacity-100 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_p]:opacity-90">
      {children}
    </div>
  </div>
);

export default PublicLayout;
